import { useRouter } from 'next/router';
import { useState, forwardRef, useImperativeHandle } from 'react';
import { useMount } from 'ahooks';
import { Form, Select, Input } from 'antd';
import css from './index.module.scss';
import { BottomArrow } from '@/components/UI/svg/Ecocredits';
import { queryShopifyCountry, queryShopifySavedAddress } from '@/service';
import { useGlobal } from '@/utils/useGlobal';
import { loginUser } from '@/utils/cookie';

interface Province {
    provinceId: string;
    provinceName: string;
    provinceCode: string;
}
interface Country {
    countryId: string;
    countryName: string;
    countryCode: string;
    provinceList: Province[];
}

interface SavedAddress {
    id: number;
    firstName: string;
    lastName: string;
    countryCode: string;
    countryName: string;
    province: string;
    provinceCode: string;
    phone: string;
    zip: string;
    city: string;
    address1: string;
    address2: string;
}

export const regxEmail = /^[A-Za-z\d]+([.%~`$^*&(){}<>?"+=!_\\-]*[A-Za-z\d-])*@[a-zA-Z\d-]{1,30}\.[a-zA-Z\d.%#~`$^*&(){}<>?"+=!_\\-]{1,70}$/;

const BuyForm = forwardRef(({}, ref) => {
    const { useLang } = useGlobal();
    const { locale } = useRouter();
    const { t } = useLang('shared');
    const { t: t1 } = useLang('ecocredits');
    const [countrys, setCountrys] = useState<Country[]>([]);
    const [provinceList, setProvinceList] = useState<Province[]>([]);
    const [savedAddresses, setSavedAddresses] = useState<SavedAddress[]>([]);
    const [form] = Form.useForm();
    const getShopifyCountry = async () => {
        const res = await queryShopifyCountry(locale as string);
        if (res?.code === '0') {
            const countrys = res?.data?.map((c: { code: any }) => ({
                ...c,
                countryCode: c.code,
            }));
            setCountrys(countrys || []);
            return countrys || [];
        }
    };

    // 获取当前州数据
    const getProvinceList = (countrys: Country[], countryCode?: string) => {
        if (!countryCode) return [];

        const currCountry = countrys?.find((country) => {
            return country?.countryCode === countryCode;
        });

        return currCountry?.provinceList || [];
    };

    const emailValidate = () => ({
        validator: async (_: any, value: string) => {
            if (!value || !value.trim()) return Promise.reject(t('common.email_validation_error'));
            if (!regxEmail.test(value)) return Promise.reject(t('common.email_validation_error'));
            return Promise.resolve();
        },
    });

    // 获取已保存地址列表
    const getSavedshopifyAddress = async (countrys: Country[]) => {
        const res = await queryShopifySavedAddress(locale as string);
        if (res?.code === '0' && res?.data?.length) {
            // 根据shopify获取的国家列表过滤地址
            const savedAddresses = res.data?.filter((address: SavedAddress) => {
                return !!countrys.find((c) => c.countryCode === address?.countryCode);
            });
            if (!savedAddresses?.length) return;

            form.setFieldsValue(savedAddresses[0]);
            setSavedAddresses(savedAddresses || []);
        }
    };

    useImperativeHandle(ref, () => ({
        getFormData: async () => {
            const formData = await form.validateFields();
            const { countryCode, provinceCode } = formData;
            const currCountry = countrys?.find((country) => country.countryCode === countryCode);
            const currProvince = provinceList?.find((province) => province?.provinceCode === provinceCode);
            const { countryName } = currCountry || {};
            const { provinceName: province } = currProvince || {};

            return {
                ...formData,
                countryName,
                province,
            };
        },
    }));

    useMount(async () => {
        const countrys = await getShopifyCountry();

        const currProvinces = getProvinceList(countrys, countrys[0]?.countryCode);
        const { email } = loginUser().user;
        // console.log('currProvinces', currProvinces);
        setProvinceList(currProvinces || []);
        form.setFieldsValue({ countryCode: countrys[0]?.countryCode, provinceCode: currProvinces[0]?.provinceCode, email: email || '' });

        // 填充已保存地址
        getSavedshopifyAddress(countrys);
    });

    return (
        <Form
            id="BUY_FORM_BOX"
            form={form}
            className={css.buy_form}
            layout="vertical"
            requiredMark={false}
            onValuesChange={(changeValue) => {
                if (!changeValue?.countryCode) return;
                setProvinceList(getProvinceList(countrys, changeValue.countryCode));
            }}
        >
            <h5 className="buy_form_title">{t('shipping_address_text')}</h5>
            {/* 地址id */}
            {!!savedAddresses?.length && (
                <Form.Item label={'Saved addreddes'} name="id">
                    <Select
                        suffixIcon={<BottomArrow />}
                        getPopupContainer={() => document.getElementById('BUY_FORM_BOX') as HTMLDivElement}
                        dropdownClassName={css.efDropdownSelect}
                        options={[
                            { label: t1('use_new_address'), value: '' },
                            ...savedAddresses?.map((address) => {
                                const { address1, address2, city, zip, countryName } = address;
                                return {
                                    label: [address1, address2, city, zip, countryName].filter((i) => !!i).join(', '),
                                    value: address.id,
                                };
                            }),
                        ]}
                        onChange={(value) => {
                            if (!value) {
                                form.setFieldsValue({
                                    id: '',
                                    firstName: undefined,
                                    lastName: undefined,
                                    phone: undefined,
                                    zip: undefined,
                                    city: undefined,
                                    address1: undefined,
                                    address2: undefined,
                                });
                                return;
                            }
                            const address = savedAddresses?.find((address) => address?.id === value);
                            form.setFieldsValue(address);
                        }}
                    />
                </Form.Item>
            )}

            {/* 国家 */}
            <Form.Item
                label={t('my.site_account_profile_country')}
                name="countryCode"
                rules={[{ required: true, message: t('user.site_account_profile_country_wrong_tip') }]}
            >
                <Select
                    suffixIcon={<BottomArrow />}
                    getPopupContainer={() => document.getElementById('BUY_FORM_BOX') as HTMLDivElement}
                    dropdownClassName={css.efDropdownSelect}
                    showSearch
                    optionFilterProp="label"
                    options={countrys?.map((country) => ({
                        label: country.countryName,
                        value: country.countryCode,
                    }))}
                />
            </Form.Item>

            <div className="group">
                <Form.Item name="firstName" rules={[{ required: true, message: t('user.site_account_profile_firstname_wrong_tip') }]}>
                    <Input placeholder={t('my.site_account_profile_firstname')} />
                </Form.Item>

                <Form.Item
                    name="lastName"
                    style={{ marginLeft: '10px' }}
                    rules={[{ required: true, message: t('user.site_account_profile_lastname_wrong_tip') }]}
                >
                    <Input placeholder={t('my.site_account_profile_lastname')} />
                </Form.Item>
            </div>

            <Form.Item name="address1" rules={[{ required: true, message: t('user.site_account_address_address_wrong_tip') }]}>
                <Input placeholder={t('my.site_account_address_adress')} />
            </Form.Item>

            <Form.Item name="address2">
                <Input placeholder={t('my.site_account_address_apt')} />
            </Form.Item>

            <div className="group">
                <Form.Item style={{ width: '100%' }} name="city" rules={[{ required: true, message: t1('city_validatino_error') }]}>
                    <Input placeholder={t('my.site_account_address_city')} />
                </Form.Item>

                {!!provinceList?.length && (
                    <Form.Item
                        style={{ width: '100%', marginLeft: '10px' }}
                        name="provinceCode"
                        label="State"
                        initialValue="State"
                        rules={[{ required: true }]}
                    >
                        <Select
                            suffixIcon={<BottomArrow />}
                            getPopupContainer={() => document.getElementById('BUY_FORM_BOX') as HTMLDivElement}
                            dropdownClassName={css.efDropdownSelect}
                            options={provinceList?.map((p) => ({
                                label: p.provinceName,
                                value: p.provinceCode,
                            }))}
                        />
                    </Form.Item>
                )}
            </div>

            <Form.Item name="zip" rules={[{ required: true, message: t('user.site_account_address_zip_wrong_tip') }]}>
                <Input placeholder={t('my.site_account_address_zip')} />
            </Form.Item>

            <Form.Item name="phone" rules={[{ required: true, message: t('user.site_account_address_phone_wrong_tip') }]}>
                <Input placeholder={t('my.site_account_address_phone')} />
            </Form.Item>

            <Form.Item name="email" rules={[emailValidate]}>
                <Input placeholder={t('my.site_account_profile_email')} />
            </Form.Item>

            {/* 隐藏数据 */}
            <Form.Item hidden name="company" initialValue={null}>
                <Input />
            </Form.Item>
            <Form.Item hidden name="customerId" initialValue={null}>
                <Input />
            </Form.Item>
        </Form>
    );
});

export default BuyForm;
